// src/modules/myvault/components/automations/actions/InstantiateTemplateAction.tsx

// What? INSTANTIATE_TEMPLATE editor: template id + params (JSON for v1).
// Why?  The backend dispatcher accepts `{template_id, params?}`. Template
//       discovery surface is not standardized across MyVault yet (document
//       templates live in a few different locations), so v1 takes a raw
//       identifier and a typed params JSON; a selector comes in v2 when
//       the platform has a single template inventory.

import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Stack,
  Textarea,
} from "@mui/joy";
import type { ActionEditorProps } from "../types";

const InstantiateTemplateAction: React.FC<ActionEditorProps> = ({
  config,
  onChange,
}) => {
  const templateId =
    typeof config.template_id === "string" ? config.template_id : "";
  const paramsJson = (() => {
    if (typeof config.params === "string") return config.params;
    if (config.params && typeof config.params === "object") {
      try {
        return JSON.stringify(config.params, null, 2);
      } catch {
        return "{}";
      }
    }
    return "{}";
  })();

  const [jsonText, setJsonText] = useState(paramsJson);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    setJsonText(paramsJson);
  }, [paramsJson]);

  const handleBlur = () => {
    try {
      const parsed = jsonText.trim() === "" ? {} : JSON.parse(jsonText);
      onChange({ params: parsed });
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : "Invalid JSON");
    }
  };

  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Template id</FormLabel>
        <Input
          size="sm"
          value={templateId}
          onChange={(e) => onChange({ template_id: e.target.value })}
          placeholder="template-uuid-or-slug"
        />
      </FormControl>

      <FormControl>
        <FormLabel>Parameters (JSON)</FormLabel>
        <Textarea
          size="sm"
          minRows={4}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          onBlur={handleBlur}
          sx={{ fontFamily: "monospace", fontSize: "12px" }}
        />
        {jsonError ? (
          <FormHelperText sx={{ color: "danger.600" }}>
            {jsonError}
          </FormHelperText>
        ) : (
          <FormHelperText>
            Params passed into the template instantiation call.
          </FormHelperText>
        )}
      </FormControl>
    </Stack>
  );
};

export default InstantiateTemplateAction;
